/**
 * @fileoverview This module defines the events that the Service Worker can send to clients.
 */

import {ServiceWorkerModel} from '../service-worker-model/service-worker.model';
import {IServiceWorkerVersion} from './service-worker.types';

/**
 * @interface IServiceWorkerEvent
 * @description Represents an event sent from the Service Worker to clients
 * @template T - The type of the event (must be a valid ServiceWorkerEventType)
 */
export interface IServiceWorkerEvent<T extends ServiceWorkerEventType = ServiceWorkerEventType> {
	eventType: T;
	payload: ServiceWorkerEventParams<T>[0]; // Assuming each event has at most one parameter
}

/**
 * @function ServiceWorkerEvents
 * @description Factory function that creates an object with methods to fire Service Worker events
 * @param {ServiceWorkerModel} swModel - The Service Worker model
 */
export const ServiceWorkerEvents = (swModel: ServiceWorkerModel) => ({
	reloadPage: () =>
		fireEvent({
			eventType: 'reloadPage',
			payload: swModel.Version.currentVersion,
		}),
	newVersionAvailable: (versionInfo: IServiceWorkerVersion) => fireEvent({eventType: 'newVersionAvailable', payload: versionInfo}),
});

/**
 * @function fireEvent
 * @description Sends an event to all connected clients
 * @param {IServiceWorkerEvent} event - The event to send
 */
function fireEvent(event: IServiceWorkerEvent): void {
	self.clients.matchAll().then(clients => {
		clients.forEach(client => client.postMessage(event));
	});
}

// Type definitions
export type ServiceWorkerEventsType = ReturnType<typeof ServiceWorkerEvents>;
export type ServiceWorkerEventType = keyof ServiceWorkerEventsType;
export type ServiceWorkerEventParams<T extends ServiceWorkerEventType> = Parameters<ServiceWorkerEventsType[T]>;

/**
 * @function isServiceWorkerEventType
 * @description Type guard to check if a value is a valid ServiceWorkerEventType
 * @param {unknown} value - The value to check
 * @returns {boolean} True if the value is a valid ServiceWorkerEventType
 */
export function isServiceWorkerEventType(value: unknown): value is ServiceWorkerEventType {
	return typeof value === 'string' && value in ServiceWorkerEvents({} as any);
}

/**
 * @function isServiceWorkerEvent
 * @description Type guard to check if an object is a valid IServiceWorkerEvent
 * @param {any} data - The object to check
 * @returns {boolean} True if the object is a valid IServiceWorkerEvent
 */
export function isServiceWorkerEvent(data: any): data is IServiceWorkerEvent {
	return data?.eventType && isServiceWorkerEventType(data.eventType);
}
